import {
  Col,
  Divider,
  Form,
  Input,
  message,
  PageHeader,
  Popover,
  Row,
  Typography,
} from "antd";
import { useTranslation } from "react-i18next";
import { RouteComponentProps } from "react-router-dom";
import {
  ExclamationCircleOutlined,
  MinusCircleOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import "./Checkout.scss";
import { useEffect, useState } from "react";
import { BRLFormat } from "shared/helpers/BRLFormat";
import MercadoPagoIcon from "shared/assets/images/mercado-pago-icon.svg";
import {
  fetchUrlCheckoutByQuantity,
  getDiagnosticPrice,
  validateCoupon,
} from "shared/service/StrmService";
import { MaskInput } from "shared/components";
import { useSelector } from "react-redux";
import { formCNPJValidator } from "shared/helpers/cnpj-validator";

const { REACT_APP_APPLICATION_URL } = process.env;
const Checkout: React.FC<RouteComponentProps> = (props) => {
  const { history } = props;
  const { t } = useTranslation("radar");
  const [form] = Form.useForm();
  const [unitPrice, setUnitPrice] = useState<number>(0);
  const [quantity, setQuantity] = useState<number>(1);
  const [maxQuantity, setMaxQuantity] = useState<number>(999);
  const [codigoCoupon, setCodigoCoupon] = useState<string>("");
  const [coupon, setCoupon] = useState<any>({ valid: false });
  const [stage, setStage] = useState("cart");
  const { loggedUser } = useSelector((state: any) => state.users);
  const [defaultForm, setDefaultForm] = useState<any>({});
  const decreaseQuantity = () => {
    if (quantity > 0) {
      setQuantity(quantity - 1);
    }
  };

  const increaseQuantity = () => {
    if (quantity < maxQuantity) setQuantity(quantity + 1);
  };

  const onFinish = async () => {
    const values = form.getFieldsValue(true);
    getUrlCheckout(values);
  };

  const onApplyClick = async () => {
    const response: any = await validateCoupon(codigoCoupon);
    if (response) {
      const percentage: number = response;
      const fullDiscount = percentage === 100;
      const discountUnitPrice = unitPrice * ((100 - percentage) / 100);
      const newMaxQuantity = fullDiscount ? 1 : 10;
      setMaxQuantity(newMaxQuantity);
      setQuantity(quantity > newMaxQuantity ? newMaxQuantity : quantity);
      setCoupon({
        valid: true,
        price: discountUnitPrice,
        percentage,
        fullDiscount,
      });
    } else {
      setCoupon({ valid: false, validationError: true });
    }
  };

  const getUrlCheckout = async (formValues: any) => {
    const returnUrl = REACT_APP_APPLICATION_URL?.includes("localhost")
      ? "local"
      : `${REACT_APP_APPLICATION_URL}/checkout-diagnostico` || "";

    const response: any = await fetchUrlCheckoutByQuantity({
      quantity,
      returnUrl: encodeURIComponent(returnUrl),
      couponCode: coupon.valid ? codigoCoupon : "",
      ...formValues,
    });

    if (coupon.fullDiscount) {
      history.push("/diagnostic", { success: true });
    } else {
      if (response?.response)
        message.error(t("pages.Checkout.checkoutURLError"));
      else window.location.href = response.purchaseRedirectUrl;
    }
  };

  const onClickNext = () => {
    if (quantity > 0) setStage("payment-method");
  };

  useEffect(() => {
    const getUnitPrice = async () => {
      const response: any = await getDiagnosticPrice();
      setUnitPrice(response);
    };
    getUnitPrice();
  }, []);

  useEffect(() => {
    if (loggedUser?.userTeam?.length > 0) {
      const { phone, cnpj } = loggedUser.userTeam[0].team;
      setDefaultForm({
        phone,
        cnpj,
      });
    }
  }, [loggedUser]);

  useEffect(() => {
    form.resetFields();
  }, [defaultForm, form]);

  return (
    <Row className="checkout-diagnostico">
      <Col xs={12} className={`left-side ${stage !== "cart" && "blur"}`}>
        <PageHeader
          onBack={() => history.push("/diagnostic")}
          title={t("pages.Checkout.back")}
        />

        <div style={{ flex: 1 }} className="content">
          <Typography.Title
            level={2}
            style={{
              borderBottom: "2px solid #000",
              display: "unset",
              paddingRight: 10,
              paddingBottom: 3,
            }}
          >
            {t("pages.Checkout.cart")}
          </Typography.Title>
          <Row
            justify="space-between"
            style={{ marginTop: 32, userSelect: "none", height: 55 }}
          >
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <span className="button-example">
                {t("pages.Diagnostic.credits")}
              </span>
              <Popover content={<>{t("pages.Checkout.creditsExplain")}</>}>
                <ExclamationCircleOutlined
                  color="#98A2B3"
                  style={{ color: "#98A2B3" }}
                />
              </Popover>
            </div>
            <div style={{ display: "flex", textAlign: "center" }}>
              <span
                onClick={decreaseQuantity}
                className={`icon-sign ${quantity === 0 && "disabled"}`}
              >
                <MinusCircleOutlined />
              </span>
              <span className="quantity">{` ${quantity} `}</span>
              <span onClick={increaseQuantity} className="icon-sign">
                <PlusCircleOutlined />
              </span>
            </div>

            <div
              style={{
                minWidth: 100,
                // textAlign: "right",
                fontSize: 18,
                color: "#E4BC5C",
              }}
            >
              {BRLFormat(unitPrice)}
            </div>
          </Row>
          <Divider />
          <Row style={{ gap: 12 }}>
            <Form.Item
              style={{ width: "50%" }}
              help={
                coupon.validationError ? t("pages.Checkout.invalidCoupon") : ""
              }
              validateStatus={coupon.validationError ? "error" : ""}
            >
              <Input
                placeholder={t("pages.Checkout.placeholder")}
                value={codigoCoupon}
                onChange={(e) => setCodigoCoupon(e.target.value?.toUpperCase())}
                disabled={coupon?.valid}
                style={{ color: "#000" }}
              />
            </Form.Item>
            <div>
              <button
                className="botao-azul-vazado"
                onClick={onApplyClick}
                disabled={coupon?.valid || !codigoCoupon}
              >
                {t("pages.Checkout.apply")}
              </button>
            </div>
            {coupon.valid && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  flex: 1,
                }}
              >
                <b style={{ fontSize: 16 }}>{coupon.percentage}% OFF</b>
              </div>
            )}
          </Row>
          <Row justify="space-between" style={{ marginTop: 32 }}>
            <div>
              <span className="button-example">
                {t("pages.Checkout.total")}
              </span>
            </div>
            <div>
              <b
                style={{ fontSize: 18, color: "#475467" }}
                className={coupon.valid && "coupon-applied"}
              >
                {BRLFormat(unitPrice * quantity)}
              </b>
              {coupon?.valid && (
                <b style={{ fontSize: 18, color: "#0086c9", display: "block" }}>
                  {BRLFormat(coupon.price * quantity)}
                </b>
              )}
            </div>
          </Row>
        </div>
        <div style={{ textAlign: "end", padding: "72px 52px" }}>
          <button className="botao-colorido" onClick={onClickNext}>
            {t("pages.Checkout.next")}
          </button>
        </div>
      </Col>
      <Col
        xs={12}
        className={`right-side ${stage !== "payment-method" && "blur"}`}
      >
        <PageHeader
          onBack={() => setStage("cart")}
          title={t("pages.Checkout.back")}
        />
        <div style={{ flex: 1 }} className="content">
          <Form
            form={form}
            layout="vertical"
            initialValues={defaultForm}
            onFinish={onFinish}
          >
            <Row gutter={32} justify="space-between">
              <Col span={12}>
                <Form.Item
                  name="phone"
                  label={t("pages.Checkout.phone")}
                  rules={[
                    {
                      required: true,
                      message: t("pages.Checkout.phoneRequired"),
                    },
                  ]}
                >
                  <MaskInput mask="(99) 9999-99999" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="cnpj"
                  label={t("pages.Checkout.cnpj")}
                  rules={[
                    {
                      required: true,
                      message: t("pages.Checkout.cnpjRequired"),
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        return formCNPJValidator(
                          value,
                          getFieldValue,
                          "cnpj",
                          t
                        );
                      },
                    }),
                  ]}
                >
                  <MaskInput mask="99.999.999/9999-99" />
                </Form.Item>
              </Col>
            </Row>
          </Form>
          <Typography.Title
            level={4}
            style={{
              color: "#fff",
            }}
          >
            {t("pages.Checkout.paymentMethod")}
          </Typography.Title>
          <div style={{ display: "flex", gap: 6 }}>
            <div className="fake-radio-button" />
            <button className="botao-mercado-pago">
              <img src={MercadoPagoIcon} alt="" /> MERCADO PAGO
            </button>
          </div>
        </div>
        <div style={{ textAlign: "end", padding: "72px 52px" }}>
          <button className="botao-colorido" onClick={() => form.submit()}>
            {t("pages.Checkout.next")}
          </button>
        </div>
      </Col>
    </Row>
  );
};

export default Checkout;
